import React, { useEffect, useState } from "react";
import styles from "./smartFilterInfoRibbon.module.scss";
import useSmartFilter from "../useSmartFilter";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import { QuestionIcon, CloseIcon, InfoIcon } from "../icons";

const TOAST_DURATION = 4000;

/**
 * Displays extracted-but-not-applied filters as dashed grey pills.
 */
const SmartFilterInfoRibbon: React.FC = () => {
  const { extractedButNotApplied, removeUnappliedFilter } = useSmartFilter();
  const { t } = useTranslationHook();

  if (!extractedButNotApplied || extractedButNotApplied.length === 0) return null;

  return (
    <div className={styles.unappliedPills}>
      {extractedButNotApplied.map((item, index) => (
        <div key={`${item.type}-${index}`} className={styles.unappliedPill}>
          <QuestionIcon />
          <span className={styles.unappliedPillLabel}>&quot;{item.value}&quot;</span>
          <span
            className={styles.unappliedPillClose}
            role="button"
            tabIndex={0}
            aria-label={t("searchpage.components.smartfilters.aria_remove_filter", {
              filter: item.value,
            })}
            onClick={() => removeUnappliedFilter(index)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") removeUnappliedFilter(index);
            }}
          >
            <CloseIcon />
          </span>
        </div>
      ))}
    </div>
  );
};

/**
 * Toast ribbon that slides up from bottom of the input area and auto-dismisses.
 * Rendered separately inside the input wrapper for proper positioning.
 */
export const SmartFilterToast: React.FC = () => {
  const { extractedButNotApplied } = useSmartFilter();
  const { t } = useTranslationHook();
  const [showToast, setShowToast] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (extractedButNotApplied && extractedButNotApplied.length > 0) {
      setShowToast(true);
      setIsExiting(false);

      const exitTimer = setTimeout(() => {
        setIsExiting(true);
      }, TOAST_DURATION);

      const hideTimer = setTimeout(() => {
        setShowToast(false);
      }, TOAST_DURATION + 300);

      return () => {
        clearTimeout(exitTimer);
        clearTimeout(hideTimer);
      };
    }
    setShowToast(false);
    return undefined;
  }, [extractedButNotApplied]);

  if (!showToast) return null;

  return (
    <div className={isExiting ? styles.toastExit : styles.toast} role="alert">
      <span className={styles.toastIcon}>
        <InfoIcon />
      </span>
      <span className={styles.toastText}>
        {t("searchpage.components.smartfilters.unapplied_toast_text")}
      </span>
    </div>
  );
};

export default wrapperHOC(SmartFilterInfoRibbon, {
  componentName: "SmartFilterInfoRibbon",
  showForChina: false,
  showForWhiteLable: true,
});
